import React, { useState, ChangeEvent, useEffect } from "react";
import { Form } from "react-bootstrap";
import ResumePicker, { ResumeFileData } from "./ResumePicker";

interface OptimizationInitModalProps {
  show: boolean;
  onHide: () => void;
  onStart: (file: ResumeFileData, jd: string) => void;
}

const OptimizationInitModal: React.FC<OptimizationInitModalProps> = ({
  show,
  onHide,
  onStart,
}) => {
  const [selectedFile, setSelectedFile] = useState<ResumeFileData | null>(null);
  const [jobDescription, setJobDescription] = useState("");
  const [charCount, setCharCount] = useState(0);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedFile(null);
      setJobDescription("");
      setCharCount(0);
    }
  }, [show]);

  const handleJobDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setJobDescription(text);
    setCharCount(text.length);
  };

  const handleStart = () => {
    if (!selectedFile) {
      alert("Please upload a resume");
      return;
    }
    if (!jobDescription.trim()) {
      alert("Please provide a Job Description");
      return;
    }
    onStart(selectedFile, jobDescription);
    onHide(); // Close modal after starting
  };

  const handleClose = () => {
    onHide(); // Call the parent's onHide function
  };

  if (!show) return null;

  return (
    <div className="rg-modal-overlay" onClick={handleClose}>
      <div className="rg-modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="rg-modal-header">
          <h2
            className="rg-modal-title"
            style={{ color: "#1f2937", fontSize: "1.25rem", fontWeight: 600 }}
          >
            Match & Optimize Profile
          </h2>
          <button className="rg-modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <p
          className="rg-modal-desc"
          style={{
            color: "#6b7280",
            fontSize: "0.875rem",
            marginBottom: "1.5rem",
          }}
        >
          Upload your resume and paste the target Job Description. JozAI will
          calculate your match score and provide tailored rewrites.
        </p>

        {/* Upload Resume Section */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              fontWeight: 600,
              color: "#374151",
              marginBottom: "0.5rem",
              display: "block",
              fontSize: "0.875rem",
            }}
          >
            Upload Resume <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <ResumePicker file={selectedFile} onChange={setSelectedFile} />
          <p
            style={{
              color: "#9ca3af",
              fontSize: "0.7rem",
              marginTop: "0.5rem",
            }}
          >
            PDF, Docx · Max 10 MB
          </p>
        </div>

        {/* Job Description Section */}
        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <label
              style={{
                fontWeight: 600,
                color: "#374151",
                fontSize: "0.875rem",
              }}
            >
              Enter Job Description:
            </label>
            <span style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
              {charCount}/2000 Characters
            </span>
          </div>
          <Form.Control
            as="textarea"
            rows={6}
            value={jobDescription}
            onChange={handleJobDescriptionChange}
            style={{
              backgroundColor: "#ffffff",
              color: "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "0.75rem",
              fontSize: "0.875rem",
              resize: "vertical",
              width: "100%",
            }}
            placeholder="Enter your job description here..."
          />
        </div>

        {/* Footer Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
            marginTop: "1.5rem",
          }}
        >
          <button
            style={{
              background: "transparent",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "0.5rem 1rem",
              color: "#6b7280",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "0.875rem",
            }}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className="interview-hub-start-btn"
            style={{
              background: "#fbbf24",
              border: "none",
              borderRadius: "8px",
              padding: "0.5rem 1.25rem",
              color: "#1a1a2e",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "0.875rem",
            }}
            disabled={!selectedFile || !jobDescription.trim()}
            onClick={handleStart}
          >
            Start Match Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptimizationInitModal;
